/**
 * Debounced text search over concept name + description.
 *
 * Reads/writes the `?q=…` URL param via `useTagFilters`, with a 250ms
 * debounce so we don't refetch the graph on every keystroke. The visible
 * input stays in sync with the URL on first mount + on outside changes
 * (e.g. "Clear all").
 */

import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTagFilters } from '@/hooks/mindspace/useTagFilters';

const DEBOUNCE_MS = 250;

export function ConceptSearchInput() {
  const { q, setFilters } = useTagFilters();
  const [draft, setDraft] = useState(q);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Outside change (e.g. user clicked "Clear all" or shared URL).
  useEffect(() => {
    setDraft(q);
  }, [q]);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const commit = (value: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setFilters({ q: value });
    }, DEBOUNCE_MS);
  };

  return (
    <div className='relative'>
      <Search className='pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
      <input
        ref={inputRef}
        // `type='text'` (not `search`) - the browser's native search-X
        // would duplicate our own clear button at the right edge.
        type='text'
        value={draft}
        onChange={e => {
          setDraft(e.target.value);
          commit(e.target.value);
        }}
        placeholder='Search concepts…'
        className={cn(
          'w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-7 text-sm text-foreground placeholder:text-muted-foreground/60',
          // A phone: 44px tall, and 16px text so iOS does not zoom the page
          // on focus (it does for anything under 16px).
          'max-md:h-11 max-md:pr-11 max-md:text-base',
          // `globals.css` puts `ring-2 ring-ring ring-offset-2` on every
          // focus-visible input via a base-layer rule - that's a harsh
          // black halo around a text field. Override with `!important`
          // to suppress; use border color as the focus indicator instead.
          '!outline-none focus:border-brand-400 focus:!outline-none focus:!ring-0 focus:!ring-offset-0 dark:focus:border-primary',
          'focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!ring-offset-0'
        )}
      />
      {draft && (
        <button
          type='button'
          aria-label='Clear search'
          onClick={() => {
            setDraft('');
            setFilters({ q: '' });
          }}
          className='absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground max-md:right-0 max-md:flex max-md:h-11 max-md:w-11 max-md:items-center max-md:justify-center'
        >
          <X className='h-3.5 w-3.5' />
        </button>
      )}
    </div>
  );
}
